import React, { RefObject, useMemo, useCallback } from 'react'
import Flatpickr from 'react-flatpickr'
import classNames from 'classnames'
import RightArrowIcon from 'transfix-icons/build/RightArrowIcon'
import { makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { FLATPICKR_DEFAULTS } from 'constants/formElements'
import { useDateRangePickerOptionBaseStyles } from '../hooks/useDateRangePickerOptionBaseStyles'
import { IDateRangePickerOption } from '../types'
import { getSelectedOptionDate } from '../utils/getSelectedOptionDate'

const useDateRangeCalendarOptionStyles = makeStyles(theme => ({
  calendarWrapper: {
    '& .flatpickr-wrapper': {
      width: '100%',
    },
    '& .flatpickr-calendar': {
      left: '100%',
      top: 0,
      transform: 'translateX(-10px)',
      padding: theme.spacing(2),
      border: `1px solid ${brandColors.coolGray4}`,

      '&::before, &::after': {
        display: 'none',
      },
    },
  },
}))

interface IDateRangeCalendarOptionProps {
  parentRef: RefObject<HTMLDivElement>
  option: IDateRangePickerOption
  selectedOption: IDateRangePickerOption | null
  onSelectOption: (option: IDateRangePickerOption) => void
}

export const DateRangeCalendarOption = ({
  onSelectOption,
  option,
  parentRef,
  selectedOption,
}: IDateRangeCalendarOptionProps) => {
  const calendarClasses = useDateRangeCalendarOptionStyles()
  const baseClasses = useDateRangePickerOptionBaseStyles()
  const buttonClasses = classNames(baseClasses.button, {
    [baseClasses.activeButton]: option.key === selectedOption?.key,
  })

  const selectedOptionDate = useMemo(() => getSelectedOptionDate(selectedOption?.dateRange), [
    selectedOption?.dateRange,
  ])

  const calendarDateSelectHandler = useCallback(
    ([startAt, endAt]: Date[]) => {
      if (!startAt || !endAt) {
        return
      }

      const dateRange = {
        startAt: startAt.toISOString(),
        endAt: endAt.toISOString(),
      }

      const currentOption = {
        ...option,
        dateRange,
      }

      onSelectOption(currentOption)
    },
    [onSelectOption, option]
  )

  const flatPickrRenderButton = useCallback(
    (_, ref) => {
      return (
        <button className={buttonClasses} ref={ref as React.Ref<HTMLButtonElement>}>
          {option.label}
          <RightArrowIcon size='medium' />
        </button>
      )
    },
    [buttonClasses, option.label]
  )

  return (
    <div className={calendarClasses.calendarWrapper} data-test='DateRangeCalendarOption'>
      <Flatpickr
        render={flatPickrRenderButton}
        options={{
          ...FLATPICKR_DEFAULTS,
          mode: 'range',
          showMonths: 2,
          appendTo: parentRef?.current as HTMLDivElement,
          static: true,
          animate: false,
        }}
        onChange={calendarDateSelectHandler}
        value={selectedOptionDate}
      />
    </div>
  )
}
